import {
  CircularProgress,
  Tabs,
  Box,
  Tab,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams, Link } from 'react-router-dom';

import Page from 'common/shared-ui/src/components/Page';
import { generateRedirectUrl } from 'common/utils/getRedirectUrl';

import {
  closeProjectById,
  downloadFile,
  getProject,
  getProjectPermissions,
  getProjectReport,
  getProjectStatistic,
  openProjectById,
} from '../../../../packages/common/api';
import ProjectCloseIcon from '../../../../packages/common/shared-ui/src/icons/ProjectCloseIcon';
import ProjectEditIcon from '../../../../packages/common/shared-ui/src/icons/ProjectEditIcon';
import ProjectOpenIcon from '../../../../packages/common/shared-ui/src/icons/ProjectOpenIcon';
import ProjectUserGroupeIcon from '../../../../packages/common/shared-ui/src/icons/ProjectUserGroupeIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { setProject } from '../../app/redux/reducers/userSlice';
import IssueFiles from '../../issuePage/components/IssueFiles/IssueFiles';
import ProjectFiles from '../ProjectFiles/ProjectFiles';

// Original class: projectsPageTtl
const StyledProjectsPageTtl = styled(Grid)(theme => ({
  marginLeft: '0px !important',
}));
// Original class: ProjectsStatusChangeButtonBox
const StyledProjectsStatusChangeButtonBox = styled(Box)(theme => ({
  display: 'flex',
  alignItems: 'baseline',
}));
// Original class: ProjectsViewChangeButton
const StyledProjectsViewChangeButton = styled(ToggleButton)(theme => ({
  border: '0px !important',
  padding: '0px !important',
}));
// Original class: ProjectsTabsWrap
const StyledProjectsTabsWrap = styled(Box)(theme => ({
  marginTop: '30px',
}));
// Original class: projectCloseMessage
const StyledProjectCloseMessage = styled(Box)(theme => ({
  margin: '30px 0 0 0 !important',
  padding: '19px 17px !important',
  alignItems: 'baseline',
  borderRadius: '16px',
  display: 'inline-flex',
  alignContent: 'center',
  backgroundColor: '#FFF',

  '& .MuiSvgIcon-root': {
    marginRight: '5px',
  },
}));
// Original class: projectFieldsWrap
const StyledProjectFieldsWrap = styled(Grid)(theme => ({
  margin: '0 0 20px 0 !important',
  alignItems: 'baseline',
  backgroundColor: '#FFF',
  borderRadius: '16px',
  padding: '19px 64px 17px 19px !important',
}));
// Original class: projectFieldsTtl
const StyledProjectFieldsTtl = styled(Typography)(theme => ({
  fontWeight: '600 !important',
  fontSize: '16px !important',
  lineHeight: '20px !important',
  color: '#082253 !important',
  marginBottom: '18px !important',
}));
// Original class: projectFieldWrap
const StyledProjectFieldWrap = styled(Box)(theme => ({
  width: '100% !important',
  marginBottom: '5px !important',

  '& p': {
    margin: 0,
  },
}));
// Original class: projectFiledTtl
const StyledProjectFiledTtl = styled(Typography)(theme => ({
  fontSize: '12px!important',
  lineHeight: '20px!important',
}));
// Original class: projectFieldRow
const StyledProjectFieldRow = styled(Box)(theme => ({
  fontSize: '12px!important',
  lineHeight: '20px!important',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
}));
// Original class: projectFieldLeft
const StyledProjectFieldLeft = styled(Box)(theme => ({
  width: '40%',
  fontWeight: 700,
  fontSize: '14px',
}));
// Original class: projectFieldRight
const StyledProjectFieldRight = styled(Box)(theme => ({
  fontSize: '14px',
  width: '60%',
  overflowWrap: 'break-word',
}));

// Original class: ProjectsListViewTableRootWrap
const StyledProjectsListViewTableRootWrap = styled(TableContainer)(theme => ({
  border: '1px solid #E4E4EF',
  borderRadius: '16px',

  '& .MuiTableCell-root': {
    border: '1px solid #E4E4EF',
    padding: '10px',
    color: '#212346',
    fontWeight: 700,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },

  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      padding: '18px 10px',
      color: '#082253',
    },
  },
}));
// Original class: projectFieldMemberWrap
const StyledProjectFieldMemberWrap = styled(Grid)(theme => ({
  width: '100% !important',
  display: 'flex',
  marginBottom: '10px !important',
  rowGap: '0px',
  rowSpacing: '0px',
  position: 'relative',

  '& .MuiSvgIcon-root': {
    width: '16px',
    left: 0,
    top: '5px',
    position: 'absolute',
  },

  '& .MuiTypography-h5': {
    width: '50%',
    paddingLeft: '20px',
  },
}));
// Original class: projectFiledValue
const StyledProjectFiledValue = styled(Typography)(theme => ({
  fontSize: '14px!important',
  lineHeight: '20px!important',
}));
// Original class: projectFieldMemberRolesWrap
const StyledProjectFieldMemberRolesWrap = styled(Box)(theme => ({
  width: '50%',

  '& .MuiTypography-h5': {
    width: '100%',
    fontSize: '12px !important',
  },
}));

const saveAs = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();

  // Очистка
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const ProjectPage = () => {
  const linkFields = ['link', 'attachment', 'project'];
  const getLocalString = useTranslation();
  const pageParams = useParams();
  const dispatch = useDispatch();
  const redmineToken = useSelector(state => state.user.redmine_access_token);
  const [projectData, setProjectData] = useState({});
  const [permissions, setPermissions] = useState('');
  const [projectUserPermissions, setProjectUserPermissions] = useState({});
  const [projectDataStatistic, setProjectDataStatistic] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [tabIndex, setTabIndex] = React.useState(0);
  const navigate = useNavigate();
  const [allFields, setAllFields] = useState([]);
  const [status, setStatus] = React.useState('');
  const handleChangeStatus = (event, nextStatus) => {
    setStatus(nextStatus);
    if (nextStatus == '1') {
      openProjectById(projectData.redmineId);
    } else {
      closeProjectById(projectData.redmineId);
    }
  };
  const handelGoLink = link => {
    window.open(link, '_self');
  };
  const handelGoTrackerLink = id => {
    window.open(link, '_blank');
  };
  const handleGoEditPage = project => {
    const projectUrl = `/projects/${pageParams.projectId}/edit`;
    navigate(projectUrl);
  };
  const handelGoUserLink = id => {
    const link = 'https://opt2.rbtechnologies.ru:9909/users/' + id;
    window.open(link, '_blank');
  };
  const handelGoProjectLink = project => {
    const projectUrl = '/projects/' + project;
    navigate(projectUrl);
  };
  const handelGoMemberLink = member => {
    const memberUrl = '/users/' + member;
    let hostUrl = generateRedirectUrl({
      host: window.location.origin,
      path: memberUrl,
      redmineToken,
    });
    window.open(hostUrl, '_blank');
  };
  const handelGoFieldLink = async field => {
    let hostUrl = '';
    if (field.fieldFormat == 'project') {
      const projectUrl = '/projects/' + field.valueId;
      navigate(projectUrl);
      return;
    }
    if (field.fieldFormat == 'attachment') {
      const response = await downloadFile(field.valueId, field.valueName);
      saveAs(response.data, field.valueName);
    } else {
      hostUrl = field.valueId;
      window.open(hostUrl);
    }
  };
  useEffect(() => {
    async function fetchProject() {
      try {
        let response = await getProject(pageParams.projectId);
        setStatus(response.status);
        const formattedCustoms = response.customs.map(item => ({
          ...item,
          custom: true,
        }));
        setAllFields(response.standards.concat(formattedCustoms));
        response.children = response.children.sort((a, b) =>
          a.name > b.name ? 1 : -1,
        );
        let projectStatistic = await getProjectStatistic(response.redmineId);
        projectStatistic.rows = projectStatistic.rows.map(item => {
          let trackerLink = `/projects/${response.redmineId}/issues?filters=issue.trackerId%3Beq%3B${item.rowId}`;
          item.columns[0].link = trackerLink;
          return item;
        });
        setProjectDataStatistic(projectStatistic);
        try {
          const data = await getProjectReport(response.redmineId);
          response.reports = data;
          setProjectData(response);
        } catch (error) {
          console.error('ERROR WITH FETCH PROJECT REPORTS', error);
        } finally {
          setIsLoading(false);
        }
        try {
          const data = await getProjectPermissions(response.redmineId);
          setProjectUserPermissions(data);
        } catch (error) {
          console.error('ERROR WITH FETCH PROJECT USER PERMISSIONS', error);
        } finally {
          setIsLoading(false);
        }
        dispatch(setProject(response));
        //window.sessionStorage.setItem('project', JSON.stringify(response));
        setIsLoading(false);
      } catch (e) {
        console.error(e);
      }
    }
    fetchProject();

    window.scrollTo(0, 0);
  }, [redmineToken, dispatch, pageParams.projectId]);
  return (
    <>
      {!isLoading ? (
        <Page title={projectData.name} width='100%'>
          <Grid
            container
            sx={{
              position: 'relative',
            }}
          >
            <StyledProjectsPageTtl
              container
              size={'grow'}
              spacing={5}
              sx={{
                marginTop: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
                display: 'flex',
                position: 'relative',
              }}
            >
              <Typography variant='h1'>{projectData.name}</Typography>
              <StyledProjectsStatusChangeButtonBox>
                {(projectUserPermissions.canEdit ||
                  projectUserPermissions.canEditMembers) &&
                status != '5' ? (
                  <IconButton onClick={handleGoEditPage}>
                    <ProjectEditIcon
                      width={18}
                      height={16}
                      viewBox='0 0 18 16'
                    />
                  </IconButton>
                ) : null}
                <ToggleButtonGroup
                  orientation='vertical'
                  value={status}
                  exclusive
                  onChange={handleChangeStatus}
                >
                  {status == '5' && projectUserPermissions.canCloseProject ? (
                    <Tooltip
                      title={getLocalString(
                        'ProjectPage',
                        'buttons',
                        'openProject',
                      )}
                    >
                      <StyledProjectsViewChangeButton
                        value='1'
                        aria-label='open'
                      >
                        <ProjectOpenIcon
                          width={14}
                          height={19}
                          viewBox='0 0 14 19'
                        />
                      </StyledProjectsViewChangeButton>
                    </Tooltip>
                  ) : null}
                  {status == '1' && projectUserPermissions.canCloseProject ? (
                    <Tooltip
                      title={getLocalString(
                        'ProjectPage',
                        'buttons',
                        'closeProject',
                      )}
                    >
                      <StyledProjectsViewChangeButton
                        value='5'
                        aria-label='close'
                      >
                        <ProjectCloseIcon
                          width={14}
                          height={19}
                          viewBox='0 0 14 19'
                        />
                      </StyledProjectsViewChangeButton>
                    </Tooltip>
                  ) : null}
                </ToggleButtonGroup>
              </StyledProjectsStatusChangeButtonBox>
            </StyledProjectsPageTtl>
          </Grid>
          {status == '5' ? (
            <StyledProjectCloseMessage>
              <ProjectCloseIcon width={14} height={19} viewBox='0 0 14 19' />{' '}
              {getLocalString('ProjectPage', 'messages', 'closedProject')}
            </StyledProjectCloseMessage>
          ) : null}
          <StyledProjectsTabsWrap>
            <Grid
              container
              sx={{
                marginTop: 5,
                gap: 5,
                position: 'relative',
              }}
            >
              <Grid size={'grow'}>
                <StyledProjectFieldsWrap
                  container
                  //spacing={5}
                  sx={{
                    position: 'relative',
                  }}
                >
                  <StyledProjectFieldsTtl variant='h3'>
                    {getLocalString(
                      'ProjectPage',
                      'sections',
                      'properties',
                      'title',
                    )}
                  </StyledProjectFieldsTtl>
                  {allFields &&
                    allFields.length > 0 &&
                    allFields.map((field, index) => (
                      <StyledProjectFieldWrap key={index}>
                        <StyledProjectFieldRow>
                          <StyledProjectFieldLeft>{`${field.fieldName}:`}</StyledProjectFieldLeft>
                          {linkFields.includes(field.fieldFormat) ? (
                            <Box
                              sx={{
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#3448FF',
                                cursor: 'pointer',
                                width: '60%',
                                overflowWrap: 'break-word',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                              onClick={() => handelGoFieldLink(field)}
                            >
                              {field.valueName}
                            </Box>
                          ) : (
                            <StyledProjectFieldRight>
                              {field.fieldId === 'description'
                                ? parse(field.valueName)
                                : field.valueName}
                            </StyledProjectFieldRight>
                          )}
                        </StyledProjectFieldRow>
                      </StyledProjectFieldWrap>
                    ))}
                </StyledProjectFieldsWrap>
                {projectUserPermissions.canViewFiles && (
                  <StyledProjectFieldsWrap
                    container
                    spacing={5}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    <ProjectFiles permissions={projectUserPermissions} />
                  </StyledProjectFieldsWrap>
                )}
              </Grid>
              <Grid size={'grow'}>
                <StyledProjectFieldsWrap
                  container
                  spacing={0}
                  sx={{
                    position: 'relative',
                  }}
                >
                  <StyledProjectFieldsTtl variant='h3'>
                    {getLocalString(
                      'ProjectPage',
                      'sections',
                      'issues',
                      'title',
                    )}
                  </StyledProjectFieldsTtl>
                  <StyledProjectsListViewTableRootWrap>
                    <Table>
                      <TableHead align='left'>
                        <TableRow>
                          {projectDataStatistic.fields.map((field, index) => (
                            <TableCell key={index}>
                              <Typography
                                variant='h4'
                                sx={{
                                  fontWeight: 700,
                                }}
                              >
                                {field.fieldName}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {projectDataStatistic.rows.map((project, index) => (
                          <TableRow key={index}>
                            {project.columns.map((field, index) => (
                              <TableCell key={index}>
                                {field.link ? (
                                  <Typography
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => handelGoLink(field.link)}
                                    variant='h4'
                                  >
                                    {field.value}{' '}
                                  </Typography>
                                ) : (
                                  <Typography variant='h4'>
                                    {field.value}{' '}
                                  </Typography>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </StyledProjectsListViewTableRootWrap>
                </StyledProjectFieldsWrap>
                <StyledProjectFieldsWrap
                  container
                  spacing={0}
                  sx={{
                    position: 'relative',
                  }}
                >
                  <StyledProjectFieldsTtl variant='h3'>
                    {getLocalString(
                      'ProjectPage',
                      'sections',
                      'subprojects',
                      'title',
                    )}
                  </StyledProjectFieldsTtl>
                  {projectData.children.map((project, index) => (
                    <StyledProjectFieldMemberWrap key={index}>
                      <Typography
                        variant='h5'
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: project.status === 1 ? '#41424E' : '#7174AC',
                        }}
                        onClick={() => handelGoProjectLink(project.id)}
                      >
                        {project.name}
                      </Typography>
                    </StyledProjectFieldMemberWrap>
                  ))}
                </StyledProjectFieldsWrap>
                <StyledProjectFieldsWrap
                  container
                  spacing={0}
                  sx={{
                    position: 'relative',
                  }}
                >
                  <StyledProjectFieldsTtl variant='h3'>
                    {getLocalString(
                      'ProjectPage',
                      'sections',
                      'members',
                      'title',
                    )}
                  </StyledProjectFieldsTtl>
                  {projectData.members.map((field, index) => (
                    <StyledProjectFieldMemberWrap key={index}>
                      {field.isGroup ? (
                        <ProjectUserGroupeIcon
                          width={16}
                          height={10}
                          viewBox='0 0 16 10'
                        />
                      ) : null}
                      <StyledProjectFiledTtl
                        variant='h5'
                        sx={{ color: field.isActive ? '#41424E' : '#7174AC' }}
                      >
                        {field.userName}
                      </StyledProjectFiledTtl>
                      <StyledProjectFieldMemberRolesWrap key={index}>
                        {field.roles.map((role, index) => (
                          <StyledProjectFiledValue key={index} variant='h5'>
                            {role.roleName}
                          </StyledProjectFiledValue>
                        ))}
                      </StyledProjectFieldMemberRolesWrap>
                    </StyledProjectFieldMemberWrap>
                  ))}
                </StyledProjectFieldsWrap>
              </Grid>
            </Grid>
          </StyledProjectsTabsWrap>
        </Page>
      ) : (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100vh',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
    </>
  );
};
export default ProjectPage;
